import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import { loadStrawStageContents } from '../../services/admin/admin-straw-stage-content.service';
import styled from 'styled-components';
import OvPageTitle from '../UI/molecules/OvPageTitle';
import TocIcon from '@mui/icons-material/Toc';
import Colours from '../../design-system/colours';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvNoContent from '../UI/molecules/OvNoContent';
import OvStrawStageContentsList from '../UI/organisms/OvStrawStageContentsList';

const ADMIN_STRAW_STAGE_CONTENTS_LIMIT = 10;

const StrawStageContents = () => {
  const { t } = useTranslation();

  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const query = queryString.stringify({
    limit: ADMIN_STRAW_STAGE_CONTENTS_LIMIT,
    offset,
    order_by: isEmpty(orderBy) ? undefined : orderBy,
    order_dir: isEmpty(orderDir) ? undefined : orderDir,
  });

  const {
    data: paginatedStrawStageContents,
    isPending: isGetStrawStageContentsPending,
    isFetching: isGetStrawStageContentsFetching,
    error: getStrawStageContentsError,
  } = useQuery({
    queryKey: ['admin-straw-stage-contents', query],
    queryFn: () => loadStrawStageContents(query),
    retry: 3,
  });

  const strawStageContents = paginatedStrawStageContents?.docs ?? [];
  const total = paginatedStrawStageContents?.total ?? 0;

  useEffect(() => {
    if (!orderBy && !orderDir) {
      setOffset(0);
      setOrderBy('created_at');
      setOrderDir('asc');
    }
  }, [setOrderDir, setOrderBy, setOffset, orderBy, orderDir]);

  const isLoading =
    isGetStrawStageContentsPending || isGetStrawStageContentsFetching;

  const goToPage = (_: any, value: number) => {
    setOffset((value - 1) * ADMIN_STRAW_STAGE_CONTENTS_LIMIT);
  };

  return (
    <Container>
      <CenterPane>
        <HeaderItems>
          <OvPageTitle
            title={t('common.strawStageContents')}
            icon={<StyledTocIcon />}
          />
        </HeaderItems>
        <ActionBar>
          <OvPaginationBar
            total={total}
            limit={ADMIN_STRAW_STAGE_CONTENTS_LIMIT}
            offset={offset}
            onChange={goToPage}
          />
        </ActionBar>

        {getStrawStageContentsError && (
          <ErrorMessageWrapper>
            <OvErrorMessage message={getStrawStageContentsError?.message} />
          </ErrorMessageWrapper>
        )}

        {strawStageContents?.length ? (
          <ContentWrapper>
            <OvStrawStageContentsList strawStageContents={strawStageContents} />
          </ContentWrapper>
        ) : !isLoading ? (
          <OvNoContent>{t('common.empty')}</OvNoContent>
        ) : null}

        {isLoading && <OvLoadingIndicator position="fixed" />}
      </CenterPane>
    </Container>
  );
};

export default StrawStageContents;

const Container = styled.div`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
  margin-left: 1rem;
  background-color: ${Colours.WHITE};
`;

const HeaderItems = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  justify-content: flex-start;
  gap: 1rem;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const StyledTocIcon = styled(TocIcon)`
  && {
    margin-right: 0.5rem;
  }
`;

const ErrorMessageWrapper = styled.div`
  margin: 1rem 0;
`;

const ContentWrapper = styled.div`
  margin-top: 1.5rem;
`;
